import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import styles from './DesktopShell.module.scss'
import { Context as AuthContext } from '../../contexts/AuthContext'
import FontAwesomeIcon from '../FontAwesomeIcon/FontAwesomeIcon'
import { imageUrls } from '../../constants'
import store from '../../utilities/localStore'
import dragonLogo from '../../assets/images/龍寶寶_babyDragonHead.jpg'

const parseIds = (pathname) => {
  const g = pathname.match(/\/groups\/([^/]+)/)
  const b = pathname.match(/\/accounting_books\/([^/]+)/)
  const groupId = g ? g[1] : null
  let bookId = b ? b[1] : null
  if (bookId === 'new') { bookId = null }
  return { groupId, bookId }
}

const DesktopShell = ({ children }) => {
  const { state: authState } = useContext(AuthContext)
  const location = useLocation()
  const history = useHistory()
  const { groupId, bookId } = parseIds(location.pathname)

  const [books, setBooks] = useState([])
  const [book, setBook] = useState(null)

  // Refetch on every navigation so the switcher reflects newly-created /
  // renamed / deleted books (the page hooks themselves only fetch on mount).
  useEffect(() => {
    if (!authState || !authState.api || !groupId) { return }
    authState.api.getAccountingBooks(groupId)
      .then((res) => {
        const bs = (res.data.accounting_books || []).map((b) => ({ imageUrl: imageUrls[b.image_id || 0], ...b }))
        setBooks(bs)
      })
      .catch(() => {})
  }, [authState, groupId, location.pathname])

  useEffect(() => {
    if (!authState || !authState.api || !groupId || !bookId) { setBook(null); return }
    authState.api.getAccountingBook(groupId, bookId)
      .then((res) => {
        const d = res.data.accounting_book
        setBook({ imageUrl: imageUrls[d.image_id || 0], ...d })
      })
      .catch(() => {})
  }, [authState, groupId, bookId])

  const go = (path) => history.push(path)
  const booksHref = `/liff_entry/groups/${groupId}/accounting_books`
  const base = bookId ? `/liff_entry/groups/${groupId}/accounting_books/${bookId}` : null

  const navItems = bookId ? [
    { label: '總覽', faicon: 'faHome', to: `${base}/overview`, match: '/overview' },
    { label: '帳目明細', faicon: 'faList', to: `${base}/payments/index`, match: '/payments' },
    { label: '分帳建議', faicon: 'faClipboard', to: `${base}/payments/summary`, match: '/payments/summary' },
    { label: '設定', faicon: 'faCog', to: `${base}/settings`, match: '/settings' },
  ] : []

  // "分帳建議" is a more specific path than "帳目明細"; resolve the active item
  // by longest matching path so /payments/summary doesn't also light up 帳目明細.
  const activeMatch = navItems
    .filter((n) => location.pathname.includes(n.match))
    .sort((a, b) => b.match.length - a.match.length)[0]

  const paymentsCount = bookId ? store.get(`accountingBookPaymentsSize-${bookId}`) : null

  const renderBook = !!bookId

  return (
    <div className={styles.shell}>
      <div className={styles.appBar}>
        <div className={styles.brand} onClick={() => go(booksHref)}>
          <span className={styles.brandMark}><img src={dragonLogo} alt="龍寶寶" /></span>
          <span className={styles.brandName}>龍寶寶分帳</span>
        </div>
        <div className={styles.appBarRight}>
          {renderBook && book ? book.name : '所有帳本'}
        </div>
      </div>

      <div className={styles.body}>
        <aside className={styles.sidebar}>
          {renderBook && (
            <button className={styles.backLink} onClick={() => go(booksHref)}>
              <FontAwesomeIcon faicon="faChevronLeft" /> 所有帳本
            </button>
          )}

          {renderBook && (
            <>
              <div className={styles.bookCard}>
                <img className={styles.bookAvatar} src={(book && book.imageUrl) || imageUrls[0]} alt="" />
                <div className={styles.bookMeta}>
                  <div className={styles.bookName}>
                    {book ? book.name : '—'}
                    {book && book.current && <FontAwesomeIcon className={styles.bookStar} faicon="fasFaStar" />}
                  </div>
                  {book && <div className={styles.bookCurrency}>{book.currency}</div>}
                </div>
              </div>

              <div className={styles.stats}>
                <div className={styles.stat}>
                  <div className={styles.statNum}>{book && book.cover_cost_users_size != null ? book.cover_cost_users_size : '—'}</div>
                  <div className={styles.statLabel}>分帳人數</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statNum}>{paymentsCount != null ? paymentsCount : '—'}</div>
                  <div className={styles.statLabel}>帳款數量</div>
                </div>
              </div>

              <nav className={styles.nav}>
                {navItems.map((n) => (
                  <div
                    key={n.label}
                    className={`${styles.navItem} ${activeMatch === n ? styles.navItemActive : ''}`}
                    onClick={() => go(n.to)}
                  >
                    <span className={styles.navIcon}><FontAwesomeIcon faicon={n.faicon} /></span>
                    {n.label}
                  </div>
                ))}
              </nav>

              <button className={styles.primaryBtn} onClick={() => go(`${base}/payments/new`)}>
                <FontAwesomeIcon faicon="faPlus" /> 新增帳款
              </button>
            </>
          )}

          {groupId && (
            <>
              <div className={styles.sectionLabel}>群組</div>
              <div
                className={`${styles.navItem} ${location.pathname.endsWith('/members') ? styles.navItemActive : ''}`}
                onClick={() => go(`/liff_entry/groups/${groupId}/members`)}
              >
                <span className={styles.navIcon}><FontAwesomeIcon faicon="faUser" /></span>
                群組成員
              </div>
            </>
          )}

          <div className={styles.sectionLabel}>{renderBook ? '切換帳本' : '帳本'}</div>
          <div className={styles.switcher}>
            {books.map((b) => (
              <div
                key={b.uuid}
                className={`${styles.switcherItem} ${b.uuid === bookId ? styles.switcherItemActive : ''}`}
                onClick={() => go(`/liff_entry/groups/${groupId}/accounting_books/${b.uuid}/overview`)}
              >
                <img className={styles.switcherAvatar} src={b.imageUrl || imageUrls[0]} alt="" />
                <span className={styles.switcherName}>{b.name}</span>
                {b.current && <FontAwesomeIcon className={styles.bookStar} faicon="fasFaStar" />}
              </div>
            ))}
            <div className={styles.switcherItem} onClick={() => go(`/liff_entry/groups/${groupId}/accounting_books/new`)}>
              <span className={styles.switcherAvatar} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#8A5414' }}>
                <FontAwesomeIcon faicon="faPlus" />
              </span>
              <span className={styles.switcherName}>新增帳本</span>
            </div>
          </div>

          <div className={styles.spacer} />
        </aside>

        <main className={styles.content}>
          {/* key by pathname so the matched page remounts on navigation and
              re-runs its data hooks (they only fetch on mount) — this is what
              makes 切換帳本 / book-switching load the new book's data. */}
          <div className={styles.contentInner} key={location.pathname}>
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default DesktopShell
